import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { SignificationItem, StonesResponse, StonePlacement } from '../../../../types/signification.types';
import { StepIndicator } from './StepIndicator';
import { NarrateStep } from './NarrateStep';
import { stonesDistance } from '../../../../utils/signification.math';
import { AUDIO } from '../../../../utils/audioFeedback';
import './StonesCapture.css';

interface StonesCaptureProps {
  item: SignificationItem;
  onComplete: (response: StonesResponse, audioBlob: Blob | null) => void;
  onClose: () => void;
  itemIndex: number;
  totalItems: number;
}

type Phase = 'place' | 'narrate';

/** Distinct neutral colors for stones — no red/green to avoid emotional bias. */
const STONE_COLORS = [
  '#D4915A', // amber
  '#5B8DB8', // ocean
  '#8B6BBF', // violet
  '#C27088', // rose
  '#3A9E8F', // teal
];

/** SVG flat icons for each stone index. */
const STONE_ICONS: React.FC<{ size: number; color: string }>[] = [
  // Circle with inner ring
  ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
      <circle cx="12" cy="12" r="5" fill={color} opacity="0.9" />
    </svg>
  ),
  // Diamond
  ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2L22 12L12 22L2 12Z" stroke={color} strokeWidth="2" fill={color} fillOpacity="0.8" />
    </svg>
  ),
  // Triangle
  ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 3L22 21H2Z" stroke={color} strokeWidth="2" fill={color} fillOpacity="0.8" />
    </svg>
  ),
  // Square
  ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="18" height="18" rx="3" stroke={color} strokeWidth="2" fill={color} fillOpacity="0.8" />
    </svg>
  ),
  // Hexagon
  ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2L21 7V17L12 22L3 17V7Z" stroke={color} strokeWidth="2" fill={color} fillOpacity="0.8" />
    </svg>
  ),
];

/** Initial positions: spread stones evenly in a circle pattern. */
function initialPositions(count: number): Array<{ x: number; y: number }> {
  const cx = 0.5, cy = 0.5, r = 0.3;
  return Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  });
}

export const StonesCapture: React.FC<StonesCaptureProps> = ({
  item,
  onComplete,
  onClose,
  itemIndex,
  totalItems,
}) => {
  const [phase, setPhase] = useState<Phase>('place');
  const [revisionCount, setRevisionCount] = useState(0);
  const [tooltipStoneId, setTooltipStoneId] = useState<string | null>(null);
  const startTimeRef = useRef(Date.now());
  const canvasRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<string | null>(null);
  const didDragRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  const hasNarrative = item.narrativeAnchor.enabled;
  const stones = item.poles; // poles are reused as stone definitions for stones mode

  // Positions: normalized [0,1]
  const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>(() => {
    const init = initialPositions(stones.length);
    const map: Record<string, { x: number; y: number }> = {};
    stones.forEach((s, i) => { map[s.id] = init[i]; });
    return map;
  });

  // Track if user has moved at least one stone
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    startTimeRef.current = Date.now();
  }, [item.id]);

  // Build placements array from current positions
  const placements = useMemo((): StonePlacement[] =>
    stones.map(s => ({
      stoneId: s.id,
      x: positions[s.id]?.x ?? 0.5,
      y: positions[s.id]?.y ?? 0.5,
    })),
    [stones, positions]
  );

  // ── Touch/mouse handlers ──

  const getCanvasRect = useCallback(() => {
    return canvasRef.current?.getBoundingClientRect() ?? { left: 0, top: 0, width: 1, height: 1 };
  }, []);

  const normalizePos = useCallback((clientX: number, clientY: number) => {
    const rect = getCanvasRect();
    return {
      x: Math.max(0.05, Math.min(0.95, (clientX - rect.left) / rect.width)),
      y: Math.max(0.05, Math.min(0.95, (clientY - rect.top) / rect.height)),
    };
  }, [getCanvasRect]);

  const handlePointerDown = useCallback((stoneId: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = stoneId;
    didDragRef.current = false;
    const rect = getCanvasRect();
    const pos = positions[stoneId];
    offsetRef.current = {
      x: e.clientX - (pos.x * rect.width + rect.left),
      y: e.clientY - (pos.y * rect.height + rect.top),
    };
    AUDIO.tap();
  }, [positions, getCanvasRect]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    didDragRef.current = true;
    setTooltipStoneId(null);
    const stoneId = draggingRef.current;
    const rect = getCanvasRect();
    const x = Math.max(0.05, Math.min(0.95, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0.05, Math.min(0.95, (e.clientY - rect.top) / rect.height));
    setPositions(prev => ({ ...prev, [stoneId]: { x, y } }));
    if (!hasInteracted) setHasInteracted(true);
  }, [getCanvasRect, hasInteracted]);

  const handlePointerUp = useCallback(() => {
    if (draggingRef.current) {
      // Tap without drag — toggle tooltip
      if (!didDragRef.current) {
        const tapped = draggingRef.current;
        setTooltipStoneId(prev => prev === tapped ? null : tapped);
      } else {
        AUDIO.tap();
        if (!hasInteracted) setHasInteracted(true);
      }
    }
    draggingRef.current = null;
  }, [hasInteracted]);

  // ── Phase handlers ──

  const handleNext = useCallback(() => {
    if (phase !== 'place') return;
    if (hasNarrative) {
      setPhase('narrate');
    } else {
      const response: StonesResponse = {
        mode: 'stones',
        itemId: item.id,
        placements,
        placementDurationMs: Date.now() - startTimeRef.current,
        revisionCount,
      };
      onComplete(response, null);
    }
  }, [phase, hasNarrative, item.id, placements, revisionCount, onComplete]);

  const handleAdjust = useCallback(() => {
    setPhase('place');
    setRevisionCount(c => c + 1);
  }, []);

  const handleNarrate = useCallback((audioBlob: Blob | null) => {
    const response: StonesResponse = {
      mode: 'stones',
      itemId: item.id,
      placements,
      placementDurationMs: Date.now() - startTimeRef.current,
      revisionCount,
    };
    onComplete(response, audioBlob);
  }, [item.id, placements, revisionCount, onComplete]);

  // ── Narrate phase ──
  if (phase === 'narrate') {
    return (
      <NarrateStep
        prompt={item.narrativeAnchor.prompt}
        required={item.narrativeAnchor.required}
        onSubmit={handleNarrate}
        onBack={handleAdjust}
      />
    );
  }

  // ── Place phase ──
  return (
    <div className="stn-container">
      <div className="stn-header">
        <StepIndicator current="place" hasNarrative={hasNarrative} />
        {totalItems > 1 && (
          <div className="stn-counter">
            STEP {itemIndex + 1} OF {totalItems}
          </div>
        )}
        <div className="stn-prompt">{item.prompt}</div>
      </div>

      <div
        className="stn-canvas"
        ref={canvasRef}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onClick={() => { if (!draggingRef.current) setTooltipStoneId(null); }}
      >
        {/* Dashed border */}
        <div className="stn-canvas-border" />


        {/* Proximity lines between nearby stones */}
        <svg className="stn-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
          {stones.map((a, i) =>
            stones.slice(i + 1).map(b => {
              const pa = positions[a.id];
              const pb = positions[b.id];
              if (!pa || !pb) return null;
              const d = stonesDistance(pa, pb);
              if (d > 0.35) return null;
              const opacity = Math.max(0.05, 0.3 - d);
              return (
                <line
                  key={`${a.id}-${b.id}`}
                  x1={pa.x * 100} y1={pa.y * 100}
                  x2={pb.x * 100} y2={pb.y * 100}
                  stroke="var(--ink, #1A1814)"
                  strokeWidth="0.4"
                  strokeDasharray="1.5,1.5"
                  opacity={opacity}
                />
              );
            })
          )}
        </svg>

        {/* Stones */}
        {stones.map((stone, i) => {
          const pos = positions[stone.id];
          if (!pos) return null;
          const color = stone.color || STONE_COLORS[i % STONE_COLORS.length];
          const Icon = STONE_ICONS[i % STONE_ICONS.length];
          const isDragging = draggingRef.current === stone.id;

          return (
            <div
              key={stone.id}
              className={`stn-stone ${isDragging ? 'stn-stone--dragging' : ''}`}
              style={{
                left: `${pos.x * 100}%`,
                top: `${pos.y * 100}%`,
              }}
              onPointerDown={(e) => handlePointerDown(stone.id, e)}
            >
              <div
                className="stn-stone-circle"
                style={{ backgroundColor: `${color}70`, borderColor: `${color}CC` }}
              >
                <Icon size={20} color={color} />
              </div>
              <span className="stn-stone-label" style={{ color, backgroundColor: `${color}35`, borderColor: `${color}60` }}>
                {stone.label}
              </span>
              {tooltipStoneId === stone.id && stone.description && (
                <div className="stn-tooltip">{stone.description}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend bar */}
      <div className="stn-legend">
        {stones.map((stone, i) => {
          const color = stone.color || STONE_COLORS[i % STONE_COLORS.length];
          const Icon = STONE_ICONS[i % STONE_ICONS.length];
          return (
            <div key={stone.id} className="stn-legend-item">
              <Icon size={14} color={color} />
              <span className="stn-legend-name" style={{ color }}>{stone.label}</span>
            </div>
          );
        })}
      </div>

      <div className="stn-hint">
        Drag stones to show relative importance. Closer together = related.
      </div>

      <div className="stn-actions">
        <button
          className="stn-next-btn"
          disabled={!hasInteracted}
          onClick={handleNext}
        >
          {hasInteracted ? "That's my placement" : 'Move a stone to continue'}
        </button>
      </div>
    </div>
  );
};
